/**
 * TipSelector — preset chips + custom input for the delivery agent tip.
 *
 * Drives delivery-agent retention (and platform NPS) by surfacing tipping
 * before checkout. Default tip is ₹0; users can choose ₹10 / ₹20 / ₹30 or
 * enter a custom value.
 */
import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { Card, css } from '../../design';

export interface TipSelectorProps {
  value: number;
  onChange: (v: number) => void;
  presets?: number[];
}

const TipSelector: React.FC<TipSelectorProps> = ({
  value,
  onChange,
  presets = [10, 20, 30],
}) => {
  const [showCustom, setShowCustom] = useState(value > 0 && !presets.includes(value));
  return (
    <Card padding="md" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Heart size={16} color={css.color.food} />
        <div style={{ fontWeight: 700, color: css.color.text }}>Tip your delivery partner</div>
      </div>
      <div style={{ fontSize: 12, color: css.color.textMuted, marginBottom: 10 }}>
        100% of your tip goes directly to your delivery partner.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {presets.map((p) => {
          const active = value === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => { onChange(p); setShowCustom(false); }}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                border: `1.5px solid ${active ? css.color.primary : css.color.border}`,
                background: active ? css.color.primarySoft : css.color.surface,
                color: active ? css.color.primaryStrong : css.color.text,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              ₹{p}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowCustom((v) => !v)}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: `1.5px solid ${showCustom ? css.color.primary : css.color.border}`,
            background: showCustom ? css.color.primarySoft : css.color.surface,
            color: showCustom ? css.color.primaryStrong : css.color.text,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          Custom
        </button>
        <button
          type="button"
          onClick={() => { onChange(0); setShowCustom(false); }}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: `1.5px solid ${value === 0 ? css.color.border : 'transparent'}`,
            background: 'transparent',
            color: css.color.textMuted,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          Skip
        </button>
      </div>
      {showCustom && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, color: css.color.text }}>₹</span>
          <input
            type="number"
            min={0}
            value={value > 0 ? value : ''}
            onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
            placeholder="Enter amount"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 8,
              border: `1px solid ${css.color.border}`,
              background: css.color.surface,
              color: css.color.text,
              fontWeight: 600,
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default TipSelector;

